/**
 * Navigation Module
 * Search panel toggle functionality
 */

const SELECTORS = {
    nav: ".c-nav",
    search: "[data-nav-search]",
    searchToggle: "[data-nav-search-toggle]",
    searchPanel: "[data-nav-search-panel]",
    searchInput: "[data-nav-search-input]",
    dropdownItem: ".c-nav__item.dropdown",
    dropdownToggle: ".c-nav__dropdown-toggle",
    dropdownLink: ".c-nav__link",
    dropdownMenu: ".c-nav__dropdown",
    dropdownMenuLink: ".c-nav__dropdown-link",
};

const CLASSES = {
    open: "is-open",
};

/**
 * Initialize navigation enhancements
 */
export function initNavigation() {
    const nav = document.querySelector(SELECTORS.nav);
    if (!nav) return;

    initSearchToggle(nav);
    initDropdownKeyboard(nav);
}

function initSearchToggle(nav) {
    const search = nav.querySelector(SELECTORS.search);
    if (!search) return;

    const toggle = search.querySelector(SELECTORS.searchToggle);
    const panel = search.querySelector(SELECTORS.searchPanel);
    const input = panel?.querySelector(SELECTORS.searchInput);

    if (!toggle || !panel || !input) return;

    const isOpen = () => search.classList.contains(CLASSES.open);

    const closeSearch = () => {
        search.classList.remove(CLASSES.open);
        toggle.setAttribute("aria-expanded", "false");
    };

    const openSearch = () => {
        search.classList.add(CLASSES.open);
        toggle.setAttribute("aria-expanded", "true");
        window.requestAnimationFrame(() => input.focus());
    };

    toggle.addEventListener("click", () => {
        if (isOpen()) {
            closeSearch();
        } else {
            openSearch();
        }
    });

    // Close on click outside
    document.addEventListener("click", (event) => {
        if (!isOpen()) return;
        if (!search.contains(event.target)) {
            closeSearch();
        }
    });

    // Close on Escape, return focus to toggle
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && isOpen()) {
            closeSearch();
            toggle.focus();
        }
    });

    // Close when mobile nav collapses
    const menu = nav.querySelector("#mainNavigation");
    if (menu) {
        menu.addEventListener("hide.bs.collapse", closeSearch);
    }
}

/**
 * Keyboard navigation for split-button dropdowns (WCAG 2.2 AA)
 * Handles: ArrowDown to open & focus first item, Escape to close & restore
 * focus, Left/Right arrows between link and toggle, Tab-out to close.
 */
function initDropdownKeyboard(nav) {
    const dropdownItems = nav.querySelectorAll(SELECTORS.dropdownItem);
    if (!dropdownItems.length) return;

    for (const item of dropdownItems) {
        const link = item.querySelector(SELECTORS.dropdownLink);
        const toggle = item.querySelector(SELECTORS.dropdownToggle);
        const menu = item.querySelector(SELECTORS.dropdownMenu);
        if (!link || !toggle || !menu) continue;

        // Import Bootstrap Dropdown for programmatic control
        const getDropdownInstance = () => {
            const Dropdown = window.bootstrap?.Dropdown;
            if (!Dropdown) return null;
            return Dropdown.getOrCreateInstance(toggle);
        };

        const isOpen = () => toggle.getAttribute("aria-expanded") === "true";

        const focusFirstMenuItem = () => {
            const firstLink = menu.querySelector(SELECTORS.dropdownMenuLink);
            if (firstLink) firstLink.focus();
        };

        // ArrowDown on toggle: open dropdown, focus first item
        toggle.addEventListener("keydown", (e) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                const instance = getDropdownInstance();
                if (instance && !isOpen()) instance.show();
                focusFirstMenuItem();
            }
        });

        // Left/Right arrows between the parent link and toggle
        link.addEventListener("keydown", (e) => {
            if (e.key === "ArrowRight") {
                e.preventDefault();
                toggle.focus();
            }
        });

        toggle.addEventListener("keydown", (e) => {
            if (e.key === "ArrowLeft") {
                e.preventDefault();
                link.focus();
            }
        });

        // Escape inside dropdown menu: close and return focus to toggle
        menu.addEventListener("keydown", (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                const instance = getDropdownInstance();
                if (instance) instance.hide();
                toggle.focus();
            }
        });

        // Close dropdown when focus leaves the entire dropdown group
        item.addEventListener("focusout", (e) => {
            // Delay to let the new activeElement settle
            requestAnimationFrame(() => {
                if (!item.contains(document.activeElement) && isOpen()) {
                    const instance = getDropdownInstance();
                    if (instance) instance.hide();
                }
            });
        });
    }
}
